import React, { useState, useMemo, useEffect } from 'react';
import {
    Tabs, Typography, Button, Row, Col, Card, Table, Tag, Modal, Form, Input, Select, message, Space, Alert,
    Switch, DatePicker
} from 'antd';
import {
    AppstoreOutlined, SwapOutlined, CalculatorOutlined, PlusOutlined, ShopOutlined, InfoCircleOutlined,
    UserOutlined, EditOutlined, StopOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import { useGetBodegas, useSaveBodega, useTrasladarStock } from '../../../hooks/useBodegas';
import { useGetProductos } from '../../../hooks/useProductos';
import { useGetHistorialInventario } from '../../../hooks/useInventario';
import { useAuth } from '../../../aws/AuthContext';
import { useGetUsuarios } from '../../../hooks/useUsuarios';
import SeccionLiquidacion from './SeccionLiquidacion';
import axios from 'axios';
import dayjs from 'dayjs';
import ModalTraslado from '../modals/ModalTraslado';

const { Title, Text } = Typography;
const { confirm } = Modal;

const DESCRIPCIONES_TIPO = {
    principal: {
        titulo: "Centro de Distribución Principal",
        texto: "Es el corazón de tu inventario. Aquí recibes la mercancía de tus proveedores y desde aquí despachas a tus otras sucursales o vehículos. Su inventario es permanente y los productos agotados siempre estarán visibles para recordarte reabastecer.",
        color: "blue"
    },
    secundaria: {
        titulo: "Sucursal Física o Local Comercial",
        texto: "Ideal para tus puntos de venta fijos. Varios cajeros pueden facturar al mismo tiempo desde este inventario. Al igual que la sede principal, su inventario es permanente y conserva el historial de los productos agotados.",
        color: "cyan"
    },
    vendedor: {
        titulo: "Ruta de Ventas o Vehículo Móvil",
        texto: "Perfecta para inventarios móviles asignados a un solo empleado. Al finalizar su turno, el sistema facilitará un cuadre de cuentas para vaciar la mercancía sobrante y dejar la lista completamente en blanco para el día siguiente.",
        color: "orange"
    }
};

const TabBodegas = () => {
    const { getJwtToken } = useAuth();
    const API_URL = process.env.REACT_APP_API_URL;

    const [activeSubTab, setActiveSubTab] = useState('mis_bodegas');
    const [tipoSeleccionado, setTipoSeleccionado] = useState('secundaria');

    // Filtro visual para bodegas inactivas
    const [mostrarInactivas, setMostrarInactivas] = useState(false);

    // Estado para edición
    const [bodegaEnEdicion, setBodegaEnEdicion] = useState(null);

    const { data: bodegas = [], isLoading: cargandoBodegas, refetch: refetchBodegas } = useGetBodegas();
    const { data: productosDB = [], isLoading: cargandoProductos } = useGetProductos();

    const crearBodega = useSaveBodega();
    const trasladarStock = useTrasladarStock();

    const [isModalBodegaOpen, setIsModalBodegaOpen] = useState(false);
    const [isModalTrasladoOpen, setIsModalTrasladoOpen] = useState(false);

    const [formBodega] = Form.useForm();
    const [formTraslado] = Form.useForm();

    const [fechaDesdeTraslados, setFechaDesdeTraslados] = useState(dayjs().startOf('month'));
    const [fechaHastaTraslados, setFechaHastaTraslados] = useState(dayjs().endOf('day'));

    // Convertimos a string (YYYY-MM-DD) para enviar al backend
    const inicioStrT = fechaDesdeTraslados.format('YYYY-MM-DD');
    const finStrT = fechaHastaTraslados.format('YYYY-MM-DD');

    const { data: historialDB = [], isLoading: cargandoHistorial } = useGetHistorialInventario(inicioStrT, finStrT);

    // Y el useMemo ahora solo filtra por el TIPO de movimiento, porque las fechas ya las filtró la base de datos
    const historialTraslados = useMemo(() => {
        return historialDB.filter(h => h.tipo_movimiento === 'traslado');
    }, [historialDB]);

    const { data: usuariosReales = [], isLoading: cargandoUsuarios } = useGetUsuarios();

    const bodegasFiltradas = useMemo(() => {
        if (mostrarInactivas) return bodegas;
        return bodegas.filter(b => b.activa);
    }, [bodegas, mostrarInactivas]);

    const opcionesProductos = useMemo(() => {
        const opciones = [];
        productosDB.filter(p => !p.es_combo).forEach(p => {
            if (p.has_variants && p.variantes?.length > 0) {
                p.variantes.forEach(v => {
                    opciones.push({ label: `${p.nombre} - ${v.atributos?.nombre || 'Var'}`, value: `var_${v.id}`, producto_id: p.id, variante_id: v.id });
                });
            } else {
                opciones.push({ label: p.nombre, value: `prod_${p.id}`, producto_id: p.id, variante_id: null });
            }
        });
        return opciones;
    }, [productosDB]);

    // ==========================================
    // GUARDAR O EDITAR BODEGA
    // ==========================================
    const handleGuardarBodega = async (values) => {
        try {
            // Validar que no exista otra bodega con el mismo nombre exacto
            const nombreNormalizado = values.nombre.trim().toLowerCase();
            const nombreDuplicado = bodegas.some(b =>
                b.nombre.toLowerCase() === nombreNormalizado &&
                (!bodegaEnEdicion || b.id !== bodegaEnEdicion.id)
            );

            if (nombreDuplicado) {
                return message.warning('Ya existe una bodega con este nombre. Elige uno diferente para evitar confusiones.');
            }

            const usuariosArray = values.usuarios_asignados
                ? (Array.isArray(values.usuarios_asignados) ? values.usuarios_asignados.map(String) : [String(values.usuarios_asignados)])
                : [];

            // Validar que ninguno de esos usuarios esté ya asignado a OTRA bodega activa
            for (const uid of usuariosArray) {
                const bodegaConflicto = bodegas.find(b =>
                    b.activa &&
                    (!bodegaEnEdicion || b.id !== bodegaEnEdicion.id) &&
                    ((Array.isArray(b.usuarios_asignados) && b.usuarios_asignados.map(String).includes(uid)) || String(b.usuario_vendedor_id) === uid)
                );
                if (bodegaConflicto) {
                    const usrObj = usuariosReales.find(u => String(u.id) === uid);
                    return message.warning(`El usuario ${usrObj?.nombre || ''} ya está asignado a la bodega "${bodegaConflicto.nombre}".`);
                }
            }

            const payloadBodega = {
                ...values,
                usuarios_asignados: usuariosArray,
                usuario_vendedor_id: usuariosArray[0] || null
            };

            if (bodegaEnEdicion) {
                const token = await getJwtToken();
                await axios.put(`${API_URL}/v1-bodegas`, {
                    id: bodegaEnEdicion.id,
                    nombre: values.nombre,
                    usuarios_asignados: usuariosArray,
                    usuario_vendedor_id: usuariosArray[0] || null
                }, { headers: { Authorization: `Bearer ${token}` } });

                message.success('Bodega actualizada correctamente');
                refetchBodegas();
            } else {
                await crearBodega.mutateAsync(payloadBodega);
                message.success('Bodega creada correctamente');
            }

            setIsModalBodegaOpen(false);
            formBodega.resetFields();
            setBodegaEnEdicion(null);
        } catch (error) {
            message.error(error.response?.data?.error || error.message || 'Error al procesar la bodega');
        }
    };

    // ==========================================
    // CAMBIAR ESTADO (ACTIVAR/DESACTIVAR)
    // ==========================================
    const handleToggleStatus = (record) => {
        const isActivating = !record.activa;

        confirm({
            title: isActivating ? '¿Reactivar Bodega?' : '¿Archivar Bodega?',
            content: isActivating
                ? `Esta bodega volverá a aparecer en las opciones de traslados y ventas.`
                : `Se ocultará de las listas principales. Nota: Solo se puede archivar si su inventario está en cero (0). Quedará un registro de seguridad de esta acción.`,
            okText: isActivating ? 'Reactivar' : 'Archivar',
            okType: isActivating ? 'primary' : 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    const token = await getJwtToken();
                    await axios.put(`${API_URL}/v1-bodegas`, {
                        id: record.id,
                        activa: isActivating
                    }, { headers: { Authorization: `Bearer ${token}` } });

                    message.success(`Bodega ${isActivating ? 'reactivada' : 'archivada'} con éxito.`);
                    refetchBodegas();
                } catch (error) {
                    message.error(error.response?.data?.error || "Error al cambiar el estado.");
                }
            }
        });
    };

    const handleTraslado = async (datos) => {
        try {
            await trasladarStock.mutateAsync({
                bodega_origen_id: datos.bodega_origen_id,
                bodega_destino_id: datos.bodega_destino_id,
                items: datos.items,
                observaciones: datos.observaciones
            });

            message.success(`Se trasladaron ${datos.items.length} productos exitosamente.`);
            setIsModalTrasladoOpen(false);
            refetchBodegas();
        } catch (error) {
            message.error(error.message || 'Error al procesar el traslado. Revisa el stock disponible.');
        }
    };

    const renderMisBodegas = () => (
        <div style={{ animation: 'fadeIn 0.3s' }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                <Col>
                    <Title level={5} style={{ margin: 0 }}>Bodegas y SubBodegas</Title>
                    <Text type="secondary">Administra tus puntos de inventario y vehículos de ruta.</Text>
                </Col>
                <Col>
                    <Space size="large">
                        <Space>
                            <Text type="secondary" style={{ fontSize: '13px' }}>Mostrar archivadas</Text>
                            <Switch checked={mostrarInactivas} onChange={setMostrarInactivas} />
                        </Space>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                            setBodegaEnEdicion(null);
                            formBodega.resetFields();
                            formBodega.setFieldsValue({ tipo: 'secundaria' });
                            setTipoSeleccionado('secundaria');
                            setIsModalBodegaOpen(true);
                        }} style={{ background: '#22BABB', borderRadius: '8px', fontWeight: 'bold' }}>
                            Nueva Bodega
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Table
                dataSource={bodegasFiltradas}
                loading={cargandoBodegas || cargandoUsuarios}
                rowKey="id"
                pagination={false}
                style={{ border: '1px solid #f0f0f0', borderRadius: '12px', overflow: 'hidden' }}
                columns={[
                    {
                        title: 'NOMBRE',
                        dataIndex: 'nombre',
                        render: (t, r) => (
                            <Space style={{ opacity: r.activa ? 1 : 0.5 }}>
                                <ShopOutlined style={{ color: r.es_default ? '#22BABB' : '#64748b' }} />
                                <Text strong style={{ fontSize: '15px' }}>{t}</Text>
                                {r.es_default && <Tag color="blue" style={{ marginLeft: 8 }}>Centro Principal</Tag>}
                            </Space>
                        )
                    },
                    {
                        title: 'TIPO DE OPERACIÓN',
                        dataIndex: 'tipo',
                        render: (t, r) => <Tag style={{ opacity: r.activa ? 1 : 0.5 }} color={t === 'vendedor' ? 'orange' : t === 'principal' ? 'blue' : 'cyan'}>{t.toUpperCase()}</Tag>
                    },
                    {
                        title: 'USUARIOS ASIGNADOS',
                        dataIndex: 'usuarios_asignados',
                        render: (lista, r) => {
                            if (r.es_default) return <Tag color="blue">Todos (Por defecto)</Tag>;

                            // Soportamos el array nuevo o el campo individual antiguo
                            const ids = Array.isArray(lista) && lista.length > 0
                                ? lista
                                : (r.usuario_vendedor_id ? [r.usuario_vendedor_id] : []);

                            if (ids.length === 0) {
                                return <Text type="secondary" style={{ fontSize: '12px' }}>Sin asignar (Usan Principal)</Text>;
                            }

                            return (
                                <Space size={[4, 4]} wrap style={{ opacity: r.activa ? 1 : 0.5 }}>
                                    {ids.map(idUser => {
                                        const u = usuariosReales.find(usr => String(usr.id) === String(idUser) || String(usr.cognito_sub) === String(idUser));
                                        return u ? (
                                            <Tag key={idUser} icon={<UserOutlined />}>
                                                {u.nombre} {u.apellidos || ''}
                                            </Tag>
                                        ) : null;
                                    })}
                                </Space>
                            );
                        }
                    },
                    {
                        title: 'STOCK (UNIDADES)',
                        dataIndex: 'stock_total_contenido',
                        align: 'center',
                        render: (v, r) => <Tag color={r.activa ? "green" : "default"} style={{ fontSize: '14px', fontWeight: 'bold', padding: '2px 10px', opacity: r.activa ? 1 : 0.5 }}>{v}</Tag>
                    },
                    {
                        title: 'ACCIONES',
                        key: 'actions',
                        align: 'center',
                        render: (_, record) => {
                            if (record.es_default) return <Text type="secondary" style={{ fontSize: '12px' }}>Protegida</Text>;

                            return (
                                <Space size="small">
                                    <Button
                                        type="text"
                                        icon={<EditOutlined style={{ color: '#22BABB' }} />}
                                        disabled={!record.activa}
                                        onClick={() => {
                                            setBodegaEnEdicion(record);
                                            const idsActuales = Array.isArray(record.usuarios_asignados) && record.usuarios_asignados.length > 0
                                                ? record.usuarios_asignados
                                                : (record.usuario_vendedor_id ? [record.usuario_vendedor_id] : []);
                                            formBodega.setFieldsValue({
                                                ...record,
                                                usuarios_asignados: record.tipo === 'vendedor' ? idsActuales[0] : idsActuales
                                            });
                                            setTipoSeleccionado(record.tipo);
                                            setIsModalBodegaOpen(true);
                                        }}
                                    />
                                    <Button
                                        type="text"
                                        danger={record.activa}
                                        style={{ color: !record.activa ? '#52c41a' : undefined }}
                                        icon={record.activa ? <StopOutlined /> : <CheckCircleOutlined />}
                                        title={record.activa ? "Archivar bodega" : "Reactivar bodega"}
                                        onClick={() => handleToggleStatus(record)}
                                    />
                                </Space>
                            );
                        }
                    }
                ]}
            />
        </div>
    );

    const renderTraslados = () => (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 280px)', animation: 'fadeIn 0.3s', overflow: 'hidden' }}>
            {/* ENCABEZADO CON ANCHO ESTRICTO */}
            <Row justify="space-between" align="middle" style={{ marginBottom: 16, flexShrink: 0, width: '100%' }}>
                <Col xs={24} sm={10} md={12}>
                    <Title level={5} style={{ margin: 0 }}>Traslados de Mercancía</Title>
                    <Text type="secondary" style={{ display: 'block' }}>Mueve inventario entre tus diferentes bodegas o vehículos.</Text>
                </Col>
                <Col xs={24} sm={14} md={12} style={{ textAlign: 'right' }}>
                    <Space style={{ width: '100%', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                        <DatePicker.RangePicker
                            value={[fechaDesdeTraslados, fechaHastaTraslados]}
                            onChange={(dates) => { if (dates) { setFechaDesdeTraslados(dates[0]); setFechaHastaTraslados(dates[1]); } }}
                            style={{ borderRadius: '8px' }}
                            allowClear={false}
                            format="DD/MM/YYYY"
                        />
                        <Button type="primary" icon={<SwapOutlined />} onClick={() => setIsModalTrasladoOpen(true)} style={{ background: '#22BABB', borderRadius: '8px', fontWeight: 'bold' }}>
                            Nuevo Traslado
                        </Button>
                    </Space>
                </Col>
            </Row>

            {/* CONTENEDOR DE LA TABLA CON SCROLL AUTOMÁTICO */}
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', border: '1px solid #f0f0f0', borderRadius: '12px' }}>
                <Table
                    dataSource={historialTraslados}
                    loading={cargandoHistorial}
                    rowKey="id"
                    pagination={{ pageSize: 50 }}
                    size="small"
                    scroll={{ x: 1000, y: 'calc(100vh - 430px)' }}
                    style={{ height: '100%' }}
                    columns={[
                        { title: 'FECHA', width: 140, dataIndex: 'fecha', render: f => dayjs(f).format('DD/MM/YYYY hh:mm A') },
                        { title: 'USUARIO', width: 180, render: (_, r) => <Text><UserOutlined /> {r.usuario_nombre ? `${r.usuario_nombre} ${r.usuario_apellidos || ''}` : 'Sistema'}</Text> },
                        { title: 'PRODUCTO', width: 250, dataIndex: 'producto_nombre', render: t => <Text strong>{t?.toUpperCase()}</Text> },
                        { title: 'CANT. TRASLADADA', width: 130, dataIndex: 'cantidad', align: 'center', render: v => <Tag color="purple" style={{ fontWeight: 'bold', margin: 0 }}>{v} Unidades</Tag> },
                        { title: 'DETALLE DEL TRASLADO', width: 350, dataIndex: 'observaciones', render: obs => <Text type="secondary">{obs}</Text> }
                    ]}
                />
            </div>
        </div>
    );

    const renderLiquidacion = () => (
        <SeccionLiquidacion bodegas={bodegasFiltradas} productosDB={productosDB} />
    );

    return (
        <div style={{ background: '#fff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
            <Tabs activeKey={activeSubTab} onChange={setActiveSubTab} type="card" items={[{ key: 'mis_bodegas', label: <span style={{ fontWeight: '600' }}><AppstoreOutlined /> Mis Bodegas</span>, children: renderMisBodegas() }, { key: 'traslados', label: <span style={{ fontWeight: '600' }}><SwapOutlined /> Traslados</span>, children: renderTraslados() }, { key: 'liquidacion', label: <span style={{ fontWeight: '600' }}><CalculatorOutlined /> Cruce de Cuentas</span>, children: renderLiquidacion() }]} />

            {/* === MODAL CREAR / EDITAR BODEGA === */}
            <Modal title={<><AppstoreOutlined /> {bodegaEnEdicion ? 'Editar Bodega' : 'Crear Nueva Bodega'}</>} open={isModalBodegaOpen} onCancel={() => setIsModalBodegaOpen(false)} footer={null} centered>
                <Form
                    layout="vertical"
                    form={formBodega}
                    onFinish={handleGuardarBodega}
                    onValuesChange={(cv) => {
                        if (cv.tipo) {
                            setTipoSeleccionado(cv.tipo);
                            formBodega.setFieldValue('usuarios_asignados', undefined);
                        }
                    }}
                >
                    <Form.Item name="nombre" label={<Text strong>Nombre de la Bodega</Text>} rules={[{ required: true, message: 'Ingrese un nombre' }]}>
                        <Input placeholder="Ej: Sede Norte, Vehículo Placa XYZ..." size="large" />
                    </Form.Item>

                    <Form.Item name="tipo" label={<Text strong>Tipo de Operación</Text>} rules={[{ required: true, message: 'Seleccione un tipo' }]}>
                        <Select size="large" disabled={!!bodegaEnEdicion}>
                            <Select.Option value="secundaria">Sucursal Física o Local Comercial</Select.Option>
                            <Select.Option value="vendedor">Ruta de Ventas o Vehículo Móvil</Select.Option>
                        </Select>
                    </Form.Item>

                    <div style={{ animation: 'fadeIn 0.3s', background: tipoSeleccionado === 'vendedor' ? '#fffbeb' : '#f8fafc', padding: '15px', borderRadius: '8px', border: tipoSeleccionado === 'vendedor' ? '1px solid #fde68a' : '1px solid #e2e8f0', marginBottom: '20px' }}>
                        <Form.Item
                            name="usuarios_asignados"
                            label={<Text strong>{tipoSeleccionado === 'vendedor' ? 'Asignar Vendedor Responsable (1 empleado)' : 'Asignar Cajeros / Usuarios a esta Bodega (Opcional)'}</Text>}
                            rules={tipoSeleccionado === 'vendedor' ? [{ required: true, message: 'Debe asignar un vendedor a esta ruta' }] : []}
                            style={{ marginBottom: 0 }}
                        >
                            <Select
                                mode={tipoSeleccionado === 'secundaria' ? 'multiple' : undefined}
                                allowClear
                                size="large"
                                placeholder={tipoSeleccionado === 'vendedor' ? "Seleccione el vendedor..." : "Seleccione uno o varios usuarios..."}
                                loading={cargandoUsuarios}
                                showSearch
                                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                            >
                                {usuariosReales.filter(u => u.status === 'active').map(u => (
                                    <Select.Option key={u.id} value={String(u.id)}>{u.nombre} {u.apellidos || ''}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <div style={{ marginTop: '12px' }}>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                {tipoSeleccionado === 'vendedor'
                                    ? 'Al facturar, este empleado descontará exclusivamente del inventario de su vehículo.'
                                    : 'Los usuarios seleccionados aquí descontarán inventario de esta sucursal al vender. Los que no tengan bodega asignada usarán la Bodega Principal.'}
                            </Text>
                            <Text type="danger" style={{ fontSize: '12px', display: 'block', marginTop: '6px', fontWeight: 'bold' }}>
                                ⚠️ Importante: Si cambias la asignación de un cajero, este deberá Cerrar Caja y volver a Iniciar Jornada para que el sistema reconozca su nueva bodega.
                            </Text>
                        </div>
                    </div>

                    <div style={{ marginBottom: '24px', animation: 'fadeIn 0.3s' }}>
                        <Alert message={<Text strong>{DESCRIPCIONES_TIPO[tipoSeleccionado]?.titulo}</Text>} description={<Text style={{ fontSize: '13px' }}>{DESCRIPCIONES_TIPO[tipoSeleccionado]?.texto}</Text>} type="info" showIcon icon={<InfoCircleOutlined style={{ color: '#0284c7' }} />} style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px' }} />
                    </div>

                    <Button type="primary" htmlType="submit" block size="large" style={{ background: '#22BABB', fontWeight: 'bold' }}>
                        {bodegaEnEdicion ? 'GUARDAR CAMBIOS' : 'CREAR BODEGA'}
                    </Button>
                </Form>
            </Modal>

            <ModalTraslado
                isOpen={isModalTrasladoOpen}
                onClose={() => setIsModalTrasladoOpen(false)}
                bodegas={bodegas}
                productos={productosDB}
                cargandoProductos={cargandoProductos}
                onTrasladar={handleTraslado}
                isPending={trasladarStock.isPending}
            />
        </div>
    );
};

export default TabBodegas;